import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useScrollToTop } from "@/lib/hooks";
import { LayoutList, Package, CalendarRange, Wrench, Users, Menu, RotateCcw, Smartphone, Search, Sun, Moon, Settings2, Sparkles, BookOpen, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useDashboard, useSession } from "@/lib/queries";
import { useTheme } from "@/lib/theme";
import { weekLabel } from "@/lib/format";
import { PrototypeBadge } from "@/components/common/PrototypeBadge";
import { CommandPalette, useCommandPalette } from "@/components/layout/CommandPalette";

const followUpCount = (d) => {
  if (!d?.follow_up) return 0;
  const ids = new Set();
  Object.values(d.follow_up).forEach((l) => (l || []).forEach((s) => ids.add(s.id)));
  return ids.size;
};

const NAV_GROUPS = [
  { label: "Arbeid", items: [
    { to: "/oversikt", label: "Oversikt", icon: LayoutList, testId: "nav-oversikt", count: followUpCount },
    { to: "/leveranser", label: "Leveranser", icon: Package, testId: "nav-leveranser", count: (d) => d?.active_deliveries },
    { to: "/plan", label: "Plan", icon: CalendarRange, testId: "nav-plan" },
  ] },
  { label: "Drift", items: [
    { to: "/service", label: "Service", icon: Wrench, testId: "nav-service", count: (d) => d?.open_service_count },
    { to: "/kunder", label: "Kunder", icon: Users, testId: "nav-kunder" },
  ] },
  { label: "Roller", items: [
    { to: "/montor", label: "Montørvisning", icon: Smartphone, testId: "nav-montor" },
  ] },
];

/* Official Trapperingen logo (SVG from trapperingen.no, navy #003C71 / blue #008FCB). B. Innvær is the member company. */
export const Wordmark = ({ compact = false }) => (
  <div className="flex min-w-0 flex-col gap-1 leading-tight" data-testid="brand-wordmark">
    <img src="/trapperingen-logo.svg" alt="Trapperingen" className={compact ? "h-[26px] w-auto" : "h-[36px] w-auto self-start"} draggable="false" />
    <span className={`font-semibold tracking-tight text-foreground ${compact ? "sr-only" : "text-[13px]"}`}>B. Innvær <span className="font-normal text-muted-foreground">· Bremnes</span></span>
  </div>
);

const ResetDialog = ({ open, onOpenChange }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    setBusy(true);
    try {
      await api.post("/session/reset");
      await qc.invalidateQueries();
      toast.success("Eksempeldata er tilbakestilt", { description: "Alle endringer i denne demoøkten er fjernet." });
      navigate("/oversikt");
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Tilbakestille eksempeldata?</AlertDialogTitle>
          <AlertDialogDescription>Alle endringer du har gjort i denne demoøkten fjernes, og eksempeldataene legges inn på nytt. Andre besøkende påvirkes ikke.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="reset-demo-cancel">Avbryt</AlertDialogCancel>
          <AlertDialogAction onClick={reset} disabled={busy} data-testid="reset-demo-confirm">Tilbakestill</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/** The Furulund walkthrough lives here – not on the work surface. */
const ExampleDialog = ({ open, onOpenChange }) => {
  const { data } = useDashboard();
  const w = data?.walkthrough;
  const steps = ["Kundebeskjed", "Registrer endring med KI-forslag", "Vurder konsekvens manuelt", "Ny spesifikasjonsrevisjon", "Kundegodkjenning (simulert)", "Intern teknisk frigivelse", "Historikk"];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Eksempelforløp · Prosjekt Furulund</DialogTitle>
          <DialogDescription>
            {w ? `${w.number} · ${w.stair_summary} · avtalt levering ${weekLabel(w.dates?.agreed_delivery_week)}.` : "TR-1042."} Tegning rev. B er godkjent og produksjonen er planlagt. Kunden spør om mørkere beis og om leveringsuken holder.
          </DialogDescription>
        </DialogHeader>
        <ol className="space-y-1.5 text-[14.5px]" data-testid="walkthrough-steps">
          {steps.map((s, i) => <li key={s} className="flex gap-3"><span className="w-5 shrink-0 tabular text-muted-foreground">{i + 1}.</span><span>{s}</span></li>)}
        </ol>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Lukk</Button>
          {w && <Button asChild data-testid="dashboard-walkthrough-open-link"><Link to={`/leveranser/${w.id}`} onClick={() => onOpenChange(false)}>Åpne {w.number} <ArrowRight /></Link></Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Demo settings: AI status, example, reset, theme. Kept out of the daily chrome. */
const DemoMenu = ({ onReset, onExample, align = "start" }) => {
  const { data: session } = useSession();
  const { isDark, toggle } = useTheme();
  const ai = session?.ai;
  const used = session?.usage?.ai_calls ?? 0;
  const max = session?.limits?.ai_calls ?? 0;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="flex h-9 w-full items-center justify-between rounded-md px-2.5 text-left transition-colors hover:bg-[hsl(var(--foreground)/0.05)]" data-testid="demo-menu-button" aria-label="Demo-innstillinger">
          <PrototypeBadge />
          <Settings2 className="h-4 w-4 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} side="top" className="w-72">
        <DropdownMenuLabel className="font-normal">
          <p className="text-[13px] font-medium">Demo · Eksempeldata</p>
          <p className="t-meta">Åpen prototype. Alle data er fiktive, og endringer gjelder bare denne økten.</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-[13px]" data-testid="ai-status">
          <p className="inline-flex items-center gap-1.5 font-medium"><Sparkles className="h-3.5 w-3.5 text-muted-foreground" /> {ai?.configured ? "KI-forslag tilgjengelig" : "KI ikke konfigurert"}</p>
          <p className="t-meta">{ai?.configured ? `Claude Sonnet 4.5 · ${used} av ${max} forslag brukt i økten` : "Forslag vises som eksempler."}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onExample} data-testid="demo-show-example"><BookOpen className="mr-2 h-4 w-4" /> Vis eksempelforløp (Furulund)</DropdownMenuItem>
        <DropdownMenuItem onClick={toggle} data-testid="theme-toggle">{isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />} {isDark ? "Lyst tema" : "Mørkt tema"}</DropdownMenuItem>
        <DropdownMenuItem onClick={onReset} data-testid="reset-demo-button"><RotateCcw className="mr-2 h-4 w-4" /> Tilbakestill eksempeldata…</DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5"><p className="t-meta" data-testid="footer-credit">Utviklet av Kviteberg</p></div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SidebarContent = ({ onNavigate, onReset, onExample, onSearch, shortcutLabel }) => {
  const { data: dash } = useDashboard();
  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-[76px] items-center border-b px-5 py-3" style={{ borderColor: "hsl(var(--sidebar-border))" }}><Wordmark /></div>
      <div className="px-3 pt-4">
        <button type="button" onClick={onSearch} className="flex h-9 w-full items-center gap-2 rounded-md border bg-background px-2.5 text-[13.5px] text-muted-foreground transition-[color,border-color] duration-150 hover:border-[hsl(var(--foreground)/0.25)] hover:text-foreground" style={{ borderColor: "hsl(var(--sidebar-border))" }} data-testid="sidebar-search-button">
          <Search className="h-4 w-4 shrink-0" /> <span className="truncate">Søk</span>
          <kbd className="kbd ml-auto shrink-0 whitespace-nowrap">{shortcutLabel}</kbd>
        </button>
      </div>
      <nav className="mt-2 flex-1 px-3" aria-label="Hovedmeny">
        {NAV_GROUPS.map((g) => (
          <div key={g.label} className="mb-1">
            <p className="nav-group">{g.label}</p>
            <div className="space-y-0.5">
              {g.items.map(({ to, label, icon: Icon, testId, count }) => {
                const n = count ? count(dash) : undefined;
                return (
                  <NavLink key={to} to={to} className="nav-link" data-testid={testId} onClick={onNavigate}>
                    <Icon /> {label}
                    {typeof n === "number" && n > 0 && <span className="nav-count" data-testid={`${testId}-count`}>{n}</span>}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="space-y-1 border-t px-3 pb-4 pt-3" style={{ borderColor: "hsl(var(--sidebar-border))" }}>
        <div className="flex items-center gap-2.5 px-2.5 pb-2 leading-tight" data-testid="user-chip">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold text-primary-foreground" style={{ background: "hsl(var(--primary))" }} aria-hidden="true">KN</span>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-medium">Kari Nordvik</p>
            <p className="t-meta truncate">Ordreansvarlig · demo-rolle</p>
          </div>
        </div>
        <DemoMenu onReset={onReset} onExample={onExample} />
      </div>
    </div>
  );
};

export const AppShell = () => {
  const [open, setOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [exampleOpen, setExampleOpen] = useState(false);
  const palette = useCommandPalette();
  const { pathname } = useLocation();
  useScrollToTop(pathname);

  return (
    <div className="min-h-screen bg-background">
      <aside className="sidebar-noise fixed inset-y-0 left-0 hidden w-[224px] border-r lg:block" style={{ backgroundColor: "hsl(var(--sidebar))", borderColor: "hsl(var(--sidebar-border))" }} data-testid="sidebar">
        <SidebarContent onReset={() => setResetOpen(true)} onExample={() => setExampleOpen(true)} onSearch={() => palette.setOpen(true)} shortcutLabel={palette.shortcutLabel} />
      </aside>
      <div className="lg:pl-[224px]">
        <header className="sticky top-0 z-40 flex h-12 items-center justify-between gap-2 border-b bg-background px-2 lg:hidden" data-testid="topbar">
          <div className="flex min-w-0 items-center gap-1">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10" aria-label="Åpne meny" data-testid="mobile-menu-button"><Menu /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0" style={{ background: "hsl(var(--sidebar))" }}>
                <SheetTitle className="sr-only">Meny</SheetTitle>
                <SidebarContent onNavigate={() => setOpen(false)} onReset={() => { setOpen(false); setResetOpen(true); }} onExample={() => { setOpen(false); setExampleOpen(true); }} onSearch={() => { setOpen(false); palette.setOpen(true); }} shortcutLabel={palette.shortcutLabel} />
              </SheetContent>
            </Sheet>
            <Wordmark compact />
          </div>
          <div className="flex items-center gap-1">
            <PrototypeBadge compact className="pr-1" />
            <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => palette.setOpen(true)} aria-label="Søk" data-testid="topbar-search-button"><Search className="h-[18px] w-[18px]" /></Button>
          </div>
        </header>
        <main><Outlet /></main>
      </div>
      <CommandPalette open={palette.open} onOpenChange={palette.setOpen} onReset={() => setResetOpen(true)} />
      <ResetDialog open={resetOpen} onOpenChange={setResetOpen} />
      <ExampleDialog open={exampleOpen} onOpenChange={setExampleOpen} />
    </div>
  );
};
